import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Hammer, MapPin, Star, Search, Plus, ArrowLeft, Briefcase, List, Map as MapIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useGeolocation } from "@/hooks/useGeolocation";
import { ArtisanSearchMap } from "@/components/map/ArtisanSearchMap";
import { WaveTopupButton } from "@/components/wallet/WaveTopupButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatFCFA = (n) => Number(n || 0).toLocaleString("fr-FR");

export default function ArtisanList({ user, onLogout }) {
  const navigate = useNavigate();
  const [specialties, setSpecialties] = useState([]);
  const [selectedSpec, setSelectedSpec] = useState(null);
  const [results, setResults] = useState([]);
  const [blocked, setBlocked] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("list");
  const geo = useGeolocation();
  const userPos = geo.position ? [geo.position.lat, geo.position.lng] : null;
  const artisans = search
    ? results.filter((a) => (a.full_name || "").toLowerCase().includes(search.toLowerCase()))
    : results;

  useEffect(() => {
    axios.get(`${API}/artisans/specialties`).then((r) => setSpecialties(r.data));
  }, []);

  // Liste gratuite ; ouvrir une fiche consomme le pass artisans (100 FCFA = 5 fiches)
  const fetchArtisans = useCallback(async () => {
    setLoading(true);
    try {
      const url = new URL(`${API}/artisans`);
      if (selectedSpec) url.searchParams.set("specialty", selectedSpec);
      if (userPos) {
        url.searchParams.set("latitude", userPos[0]);
        url.searchParams.set("longitude", userPos[1]);
      }
      const r = await axios.get(url.toString());
      setResults(r.data);
      setBlocked(null);
    } catch (e) {
      const status = e.response?.status;
      if (status === 402 || status === 401) {
        setResults([]);
        setBlocked({ status, message: e.response?.data?.detail });
      }
    } finally {
      setLoading(false);
    }
  }, [selectedSpec, userPos?.[0], userPos?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchArtisans();
  }, [fetchArtisans]);

  return (
    <div className="min-h-screen bg-[#0F172A] text-white" data-testid="artisans-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#003380] via-[#003594] to-[#D4AF37] px-4 md:px-8 pt-6 pb-10">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Button
              onClick={() => navigate("/home")}
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 rounded-full"
              data-testid="artisans-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Hammer className="w-7 h-7" />
            <h1
              className="text-3xl md:text-4xl font-black tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Artisans ARTEKNOS
            </h1>
          </div>
          <p className="text-white/80 max-w-2xl text-sm md:text-base">
            Trouvez l'artisan parfait pour vos projets. Paiement sécurisé via escrow Wave —
            les fonds ne sont libérés qu'après votre validation.
          </p>

          <div className="mt-6 flex gap-3 flex-wrap">
            <Button
              onClick={() => navigate("/artisans/new")}
              className="bg-white text-[#003594] hover:bg-white/90 font-bold rounded-full"
              data-testid="new-project-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Publier une demande
            </Button>
            {user?.role !== "client" && (
              <Button
                onClick={() => navigate("/artisans/profile")}
                variant="outline"
                className="bg-white/10 border-white/40 text-white hover:bg-white/20 rounded-full"
                data-testid="my-profile-btn"
              >
                <Briefcase className="w-4 h-4 mr-2" />
                Mon profil artisan
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Search + filters */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 -mt-5 relative z-10">
        <Card className="bg-[#1A1A1A] border-white/10 border">
          <CardContent className="py-4">
            <div className="flex items-center gap-3 mb-4">
              <Search className="w-5 h-5 text-white/60" />
              <Input
                placeholder="Rechercher un artisan par nom…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-transparent border-white/20 text-white placeholder:text-white/40"
                data-testid="search-artisan-input"
              />
            </div>
            <div className="flex gap-2 flex-nowrap overflow-x-auto pb-1 md:flex-wrap md:overflow-visible">
              <button
                onClick={() => setSelectedSpec(null)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap flex-shrink-0 transition-all ${
                  !selectedSpec
                    ? "bg-[#003594] text-white"
                    : "bg-white/10 text-white/70 hover:bg-white/20"
                }`}
                data-testid="spec-all"
              >
                Toutes spécialités
              </button>
              {specialties.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setSelectedSpec(s.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap flex-shrink-0 transition-all ${
                    selectedSpec === s.key
                      ? "bg-[#D4AF37] text-[#0F172A]"
                      : "bg-white/10 text-white/70 hover:bg-white/20"
                  }`}
                  data-testid={`spec-${s.key}`}
                >
                  <span className="mr-1.5">{s.icon}</span>
                  {s.label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Artisans list / map */}
      <div className="max-w-6xl mx-auto px-4 md:px-8 mt-6 mb-12">
        {blocked && (
          <div className="mb-6 bg-[#1a0f12] border border-red-500/40 rounded-[2rem] p-6 space-y-4" data-testid="artisan-search-blocked">
            <p className="text-sm text-white/90" data-testid="artisan-search-blocked-msg">
              {blocked.status === 401 ? "Connectez-vous pour rechercher un artisan (100 FCFA par recherche)." : blocked.message}
            </p>
            {blocked.status === 401 ? (
              <Button onClick={() => navigate("/auth")} className="rounded-full bg-[#D4AF37] text-[#0F172A]" data-testid="artisan-search-login-btn">Se connecter</Button>
            ) : (
              <WaveTopupButton user={user} defaultWallet="credits" onDone={fetchArtisans} testId="artisan-search-wave-btn" />
            )}
          </div>
        )}
        {!blocked && <p className="text-xs text-white/70 mb-3" data-testid="artisan-search-fee-note">Recherche gratuite · 100 FCFA donnent accès à 5 fiches d'artisans (coordonnées incluses).</p>}
        <div className="flex justify-end mb-4">
          <div className="inline-flex bg-white/5 border border-white/10 rounded-full p-1" data-testid="artisans-view-toggle">
            {[["list", "Liste", List], ["map", "Carte", MapIcon]].map(([key, label, Icon]) => (
              <button
                key={key}
                type="button"
                onClick={() => setView(key)}
                className={`px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest flex items-center gap-1.5 transition-colors ${view === key ? "bg-[#D4AF37] text-[#0F172A]" : "text-white/60 hover:text-white"}`}
                data-testid={`artisans-view-${key}`}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>
        </div>
        {view === "map" && (
          <ArtisanSearchMap
            artisans={artisans}
            userPos={geo.position}
            status={geo.status}
            onRequest={geo.request}
            specialties={specialties}
            onOpen={(a) => navigate(`/artisans/${a.user_id}`)}
          />
        )}
        {view === "list" && loading && (
          <p className="text-white/60 text-center py-10">Chargement…</p>
        )}
        {view === "list" && !loading && !blocked && artisans.length === 0 && (
          <Card className="bg-[#1A1A1A] border-white/10 border" data-testid="artisans-empty">
            <CardContent className="py-12 text-center">
              <Hammer className="w-12 h-12 text-white/30 mx-auto mb-3" />
              <p className="text-white/70">Aucun artisan ne correspond à votre recherche.</p>
              <p className="text-white/50 text-sm mt-2">
                Essayez une autre spécialité ou élargissez votre zone.
              </p>
            </CardContent>
          </Card>
        )}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${view === "map" ? "hidden" : ""}`}>
          {artisans.map((a) => (
            <Card
              key={a.id}
              className="bg-[#1A1A1A] border-white/10 border hover:border-[#D4AF37]/40 transition-all cursor-pointer group"
              onClick={() => navigate(`/artisans/${a.user_id}`)}
              data-testid={`artisan-card-${a.user_id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#003594] to-[#D4AF37] flex items-center justify-center text-white font-bold text-xl shadow-lg">
                    {(a.full_name || "A").charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-white truncate">{a.full_name || "Artisan"}</p>
                    <div className="flex items-center gap-2 text-xs text-white/60 mt-0.5">
                      <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                      <span>{(a.rating || 5.0).toFixed(1)}</span>
                      <span>·</span>
                      <span>{a.completed_projects || 0} projets</span>
                    </div>
                    {a.distance_km !== undefined && (
                      <p className="text-[11px] text-[#D4AF37] mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {a.distance_km} km
                      </p>
                    )}
                  </div>
                  {a.match_score && (
                    <div className="bg-gradient-to-br from-yellow-400 to-amber-500 text-white text-xs font-black px-2.5 py-1 rounded-full">
                      {Math.round(a.match_score)}%
                    </div>
                  )}
                </div>

                {a.bio && (
                  <p className="text-sm text-white/70 line-clamp-2 mb-3">{a.bio}</p>
                )}

                <div className="flex flex-wrap gap-1.5 mb-3">
                  {(a.specialties || []).slice(0, 3).map((s) => {
                    const spec = specialties.find((x) => x.key === s);
                    return (
                      <span
                        key={s}
                        className="text-[11px] px-2 py-0.5 rounded-full bg-[#003594]/30 text-[#D4AF37] font-medium"
                      >
                        {spec?.icon} {spec?.label || s}
                      </span>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  <div className="text-xs text-white/60">
                    {a.daily_rate > 0 && <span>{formatFCFA(a.daily_rate)} FCFA / jour</span>}
                    {!a.daily_rate && a.hourly_rate > 0 && (
                      <span>{formatFCFA(a.hourly_rate)} FCFA / heure</span>
                    )}
                  </div>
                  <span className="text-xs font-semibold text-[#D4AF37] group-hover:text-white transition-colors">
                    Voir profil →
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {onLogout && (
        <div className="text-center pb-8">
          <Button variant="ghost" size="sm" onClick={onLogout} className="text-white/40 hover:text-white">
            Déconnexion
          </Button>
        </div>
      )}
    </div>
  );
}
